import React from "react";
import { Typography, Card, Space, Alert, Row, Col, Steps } from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  MobileOutlined,
  UserDeleteOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useLanguage } from "../Home/LanguageContext";

const { Title, Paragraph } = Typography;
const { Step } = Steps;

const DeleteAccountSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section id="delete-account" className="public-section public-delete-section">
      <header className="public-hero public-hero-red">
        <DeleteOutlined className="public-hero-icon" />
        <h1 className="public-hero-title">{t("deleteTitle")}</h1>
        <p className="public-hero-desc">{t("deleteIntro")}</p>
      </header>

      <Alert
        className="public-alert public-alert-warning"
        message={t("deleteImportantTitle").replace(/^⚠️\s*/, "")}
        description={t("deleteImportantDesc")}
        type="warning"
        icon={<MobileOutlined />}
        showIcon
      />

      <Card
        className="public-card"
        title={
          <Space>
            <UserDeleteOutlined />
            <span>{t("howToDeleteTitle")}</span>
          </Space>
        }
      >
        <Steps direction="vertical" size="small" current={-1} className="public-steps">
          <Step title={t("stepOpenAppTitle")} description={t("stepOpenAppDesc")} icon={<MobileOutlined />} />
          <Step title={t("stepGoSettingsTitle")} description={t("stepGoSettingsDesc")} icon={<UserDeleteOutlined />} />
          <Step title={t("stepFindAccountTitle")} description={t("stepFindAccountDesc")} icon={<ExclamationCircleOutlined />} />
          <Step title={t("stepSelectDeleteTitle")} description={t("stepSelectDeleteDesc")} icon={<DeleteOutlined />} />
        </Steps>
      </Card>

      <Card
        className="public-card"
        title={
          <Space>
            <ExclamationCircleOutlined />
            <span>{t("whatDeletedTitle")}</span>
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Card size="small" className="public-card public-card-nested" title={t("personalDataTitle")}>
              <ul className="public-list">
                <li>{t("personalProfileInfo")}</li>
                <li>{t("personalEmailCredentials")}</li>
                <li>{t("personalLangPrefsProgress")}</li>
                <li>{t("personalStatsAchievements")}</li>
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="public-card public-card-nested" title={t("conversationsContentTitle")}>
              <ul className="public-list">
                <li>{t("conversationsWithPartners")}</li>
                <li>{t("chatHistoryAI")}</li>
                <li>{t("sharedMediaVoice")}</li>
                <li>{t("learningSessionRecordings")}</li>
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="public-card public-card-nested" title={t("socialConnectionsTitle")}>
              <ul className="public-list">
                <li>{t("partnerConnections")}</li>
                <li>{t("friendListsBlocked")}</li>
                <li>{t("groupMemberships")}</li>
                <li>{t("favoritesSavedContent")}</li>
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" className="public-card public-card-nested" title={t("aiCharacterDataTitle")}>
              <ul className="public-list">
                <li>{t("customAIConfigs")}</li>
                <li>{t("aiConversationHistory")}</li>
                <li>{t("personalizedRecommendations")}</li>
                <li>{t("aiGeneratedContent")}</li>
              </ul>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card
        className="public-card public-card-highlight"
        title={
          <Space>
            <ExclamationCircleOutlined />
            <span>{t("beforeDeleteTitle")}</span>
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <div className="public-stat-block">
              <Title level={4}>{t("exportYourDataTitle")}</Title>
              <Paragraph>{t("exportYourDataDesc")}</Paragraph>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="public-stat-block">
              <Title level={4}>{t("considerBreakTitle")}</Title>
              <Paragraph>{t("considerBreakDesc")}</Paragraph>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="public-stat-block">
              <Title level={4}>{t("contactSupportTitle")}</Title>
              <Paragraph>{t("contactSupportDesc")}</Paragraph>
            </div>
          </Col>
        </Row>
      </Card>

      <Card
        className="public-card public-card-warm"
        title={
          <Space>
            <MailOutlined />
            <span>{t("needHelpTitle")}</span>
          </Space>
        }
      >
        <div className="public-center">
          <Paragraph>{t("needHelpIntro")}</Paragraph>
          <Paragraph>
            <a className="public-link" href="mailto:support@linguatalk.com">
              support@linguatalk.com
            </a>
          </Paragraph>
          <Paragraph className="public-muted">{t("supportResponseTime")}</Paragraph>
          <ul className="public-list public-list-center">
            <li>{t("helpDeletionTroubleshooting")}</li>
            <li>{t("helpDataExport")}</li>
            <li>{t("helpAlternativeSolutions")}</li>
            <li>{t("helpPrivacySecurity")}</li>
          </ul>
        </div>
      </Card>

      <Alert
        className="public-alert public-alert-info"
        message={t("securityNoteTitle").replace(/^🔒\s*/, "")}
        description={t("securityNoteDesc")}
        type="info"
        showIcon
      />
    </section>
  );
};

export default DeleteAccountSection;
